import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Smartphone, Globe, Zap, Lock, Headphones } from 'lucide-react';
import { mockData } from '../../mock/data';
import { useTranslation } from '../../hooks/useTranslation';

const FeaturesSection = ({ title, subtitle, features, columns = 3, className = "" }) => {
  const { features: defaultFeatures } = mockData;
  const { t } = useTranslation();
  
  const titleText = title || t('features.title');
  const subtitleText = subtitle || t('features.subtitle');
  
  const featuresToRender = features || defaultFeatures.map((feature, index) => {
    const translatedFeatures = t('features.features');
    const translatedFeature = translatedFeatures[index] || feature;
    return { ...feature, ...translatedFeature };
  });

  const iconMap = {
    Shield: Shield,
    Smartphone: Smartphone,
    Globe: Globe,
    Zap: Zap,
    Lock: Lock,
    Headphones: Headphones
  };

  const getGridClass = () => {
    switch (columns) {
      case 2:
        return "grid-cols-1 md:grid-cols-2";
      case 4:
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
      default:
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    }
  };

  return (
    <section className={`py-24 ${className}`} style={{background: '#121211'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={{color: '#ffffff'}}>
            <span dangerouslySetInnerHTML={{ __html: titleText }} /> {(!title && <span className="text-cyan-400">IP9</span>)}
          </h2>
          <p className="text-xl max-w-3xl mx-auto" style={{color: '#ffffff'}}>
            {subtitleText}
          </p>
        </div>

        {/* Features Grid */}
        <div className={`grid ${getGridClass()} gap-px bg-slate-800 border border-slate-800`}>
          {featuresToRender.map((feature, index) => {
            const IconComponent = iconMap[feature.icon] || Shield;
            const Wrapper = feature.link ? Link : 'div';
            const wrapperProps = feature.link ? { to: feature.link } : {};
            
            return (
              <Wrapper
                key={index}
                {...wrapperProps}
                className="group relative p-8 bg-slate-950 hover:bg-slate-900 transition-colors duration-300 block"
              >
                <div className="flex items-center justify-center w-12 h-12 border border-slate-700 group-hover:border-cyan-500 mb-6 transition-colors duration-300">
                  <IconComponent className="w-6 h-6 text-cyan-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;